import { snackbar } from "@mobiscroll/react5";
import { SignalingClient } from "amazon-kinesis-video-streams-webrtc";
import React, { useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Card } from "react-bootstrap";
import { BiMicrophoneOff } from "react-icons/bi";
import { BsFillMicFill } from "react-icons/bs";
import { MdCallEnd, MdVideocam, MdVideocamOff } from "react-icons/md";
// import { useParams } from "react-router-dom";
import videocallIcon from "src/assets/videocall.svg";
import useQuery from "src/lib/useQuery";
import { getVideoCallCredentials, sendVideoCallEndNotification, sendVideoCallStartNotification, } from "./api";
import "./videocall.scss";

import { getConsultationDetails } from "../api";

const OPTIONS = {
  TRAVERSAL: { STUN_TURN: "stunTurn", TURN_ONLY: "turnOnly", DISABLED: "disabled", },
  ROLE: { VIEWER: "VIEWER", MASTER: "MASTER", },
  RESOLUTION: { WIDESCREEN: "widescreen", FULLSCREEN: "fullscreen", },
};


function VideoCall() {
  const query = useQuery();
  const appointmentId = query.get("appointmentId");
  // const { patientId } = useParams();
  const [role, setRole] = useState(OPTIONS.ROLE.MASTER);
  const [sendVideo, setSendVideo] = useState(true);
  const [sendAudio, setSendAudio] = useState(true);
  const [useTrickleICE] = useState(true);

  const localView = useRef(null);
  const remoteView = useRef(null);

  const videoStreamRef = useRef({
    signalingClient: null,
    peerConnection: null,
    peerConnectionByClientId: {},
  });

  const messageChannelRef = useRef({ dataChannel: null, });

  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);

  const channelARNRef = useRef(null);
  const roleRef = useRef(OPTIONS.ROLE.MASTER);

  // Track disconnect retry attempts
  const disconnectRetryCountRef = useRef({});
  const disconnectTimeoutRef = useRef({});

  const videoStream = videoStreamRef.current;
  const messageChannel = messageChannelRef.current;

  // Queue for ICE candidates that arrive before the PeerConnection is ready
  const pendingIceCandidatesRef = useRef({});

  const initChannel = async () => {
    const res = await getConsultationDetails({ id: appointmentId });
    if (res != null && res?.consultationType === "Online") {
      createChannel().then(() => {
        sendVideoCallStartNotification(appointmentId);
      });
    } else {
      window.open("", "_self").close();
    }
  };

  React.useEffect(() => {
    initChannel();

    // Cleanup function to prevent memory leaks
    return () => {
      console.log("VideoCall component unmounting, cleaning up...");

      (async () => {
        await cleanupResources();
      })();

      channelARNRef.current = null;

      // Clear disconnect retry tracking
      Object.keys(disconnectTimeoutRef.current).forEach(key => {
        clearTimeout(disconnectTimeoutRef.current[key]);
      });
      disconnectRetryCountRef.current = {};
      disconnectTimeoutRef.current = {};
    };
  }, []);

  const createChannel = async () => {
    try {
      const videoCallData = await getVideoCallCredentials(appointmentId);

      if (!videoCallData) {
        throw new Error("Failed to get video call credentials");
      }

      console.log("[VIDEO CALL] Received credentials from backend");

      // Store channel ARN
      channelARNRef.current = videoCallData.channelArn;

      // Web portal is always MASTER (doctor)
      console.log("[VIDEO CALL] Connecting as MASTER");
      setRole(OPTIONS.ROLE.MASTER);
      roleRef.current = OPTIONS.ROLE.MASTER;

      await startVideoCall(videoCallData);
    } catch (err) {
      console.error("Error initializing video call:", err);
      snackbar({ message: "Failed to initialize video call. Please try again.", color: "danger", });
      // Close the window after showing error
      setTimeout(() => {
        window.open("", "_self").close();
      }, 2000);
    }
  };

  const createMessageChannel = async () => {
    if (!videoStream.peerConnection) return;
    if (messageChannel.dataChannel && messageChannel.dataChannel.readyState === 'open') {
      return;
    }

    messageChannel.dataChannel = await videoStream.peerConnection.createDataChannel(
      `${appointmentId}InternalMessage`,
      { id: 1, maxRetransmits: 0, ordered: false }
    );

    messageChannel.dataChannel.onerror = (error) => {
      console.error("dataChannel.onerror", error);
    };

    messageChannel.dataChannel.onopen = () => {
      console.log("dataChannel.onopen");
    };

    messageChannel.dataChannel.onclose = () => {
      console.log("dataChannel.onclose");
    };

    videoStream.peerConnection.ondatachannel = (event) => {
      event.channel.onmessage = (event) => {
        console.log("dataChannel.onmessage:", event.data);
      };
    };
  };

  const sendMessage = (data) => {
    try {
      if (messageChannel.dataChannel && messageChannel.dataChannel.readyState === 'open') {
        messageChannel.dataChannel.send(data);
      } else {
        console.warn("DataChannel not ready, message not sent:", data);
      }
    } catch (e) {
      console.error("Send DataChannel error:", e.toString());
    }
  };

  const cleanupResources = async () => {
    console.log("Cleaning up all video resources...");

    // 1. Close Signaling
    if (videoStream.signalingClient) {
      videoStream.signalingClient.close();
      videoStream.signalingClient = null;
    }

    // 2. Close Peer Connections
    if (videoStream.peerConnection) {
      videoStream.peerConnection.close();
      videoStream.peerConnection = null;
    }
    Object.keys(videoStream.peerConnectionByClientId || {}).forEach((clientId) => {
      const pc = videoStream.peerConnectionByClientId[clientId];
      if (pc) pc.close();
    });
    videoStream.peerConnectionByClientId = {};

    // 3. Stop Camera and Mic
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }

    // 4. Clear Video Elements (Prevents frozen frames)
    if (localView.current) localView.current.srcObject = null;
    if (remoteView.current) remoteView.current.srcObject = null;
  };

  const exitCall = async () => {
    try {
      // If you are the Master, tell the backend the session is officially over
      if (roleRef.current === OPTIONS.ROLE.MASTER) {
        await sendVideoCallEndNotification({ appointmentId });
      }

      await cleanupResources();

      snackbar({ message: "Call ended successfully", color: "success" });
      window.open("", "_self").close();
    } catch (error) {
      console.error("Error during exit:", error);
      await cleanupResources();
      window.open("", "_self").close();
    }
  };

  // Helper function to setup peer connection event handlers
  const setupPeerConnection = (remoteClientId, configuration) => {
    const pc = new RTCPeerConnection(configuration);

    // Store in refs
    videoStream.peerConnection = pc;
    videoStream.peerConnectionByClientId[remoteClientId] = pc;

    // Attach local media immediately
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => {
        pc.addTrack(track, localStreamRef.current);
      });
      console.log(`[MASTER] Local tracks attached for client: ${remoteClientId}`);
    }

    pc.addEventListener("icecandidate", ({ candidate }) => {
      if (candidate && useTrickleICE && videoStream.signalingClient) {
        console.log(`[MASTER] Sending ICE candidate to viewer: ${remoteClientId}`);
        videoStream.signalingClient.sendIceCandidate(candidate, remoteClientId);
      }
    });

    pc.addEventListener("track", (event) => {
      console.log("[MASTER] Track event received from Android");
      const stream = event.streams?.[0];
      if (!stream) return;

      remoteStreamRef.current = stream;

      if (remoteView.current) {
        // FIX: Ensure we don't reset the srcObject if it's already playing this stream
        if (remoteView.current.srcObject !== stream) {
          remoteView.current.srcObject = stream;
        }

        // Use onloadedmetadata to ensure the video engine is ready to render
        remoteView.current.onloadedmetadata = () => {
          remoteView.current.play().catch((e) => {
            if (e.name !== 'AbortError') console.warn("Play error:", e);
          });
        };
      }
    });

    setupConnectionStateHandlers(OPTIONS.ROLE.MASTER, remoteClientId, pc);
    return pc;
  };

  // --- Entirely Refactored startVideoCall Function ---
  const startVideoCall = async (videoCallData) => {
    try {
      // 1. Initialize Local Media FIRST
      console.log("[MASTER] Requesting local camera/mic...");
      const localStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      localStreamRef.current = localStream;
      if (localView.current) localView.current.srcObject = localStream;

      // 2. Prepare Connection Configuration
      const urlObj = new URL(videoCallData.signedUrl);
      const channelEndpoint = `${urlObj.protocol}//${urlObj.host}`;

      // Explicitly extract region from ARN to prevent mismatch
      const arnParts = videoCallData.channelArn.split(':');
      const region = arnParts[3] || "ap-south-1";
      console.log(`[MASTER] Connecting to Region: ${region} | Role: MASTER`);

      const iceServers = [
        { urls: "stun:stun.l.google.com:19302" },
        ...(videoCallData.iceServers || [])
          .filter((s) => s.urls && s.urls.length > 0)
          .map((s) => ({
            urls: s.urls,
            username: s.username,
            credential: s.credential,
          })),
      ];

      const configuration = { iceServers, iceTransportPolicy: "all" };

      // 3. Initialize Signaling Client
      const signalingClient = new SignalingClient({
        channelARN: videoCallData.channelArn,
        channelEndpoint,
        role: OPTIONS.ROLE.MASTER,
        region,
        requestSigner: { getSignedURL: async () => videoCallData.signedUrl },
      });

      // --- CRITICAL DEBUG: This MUST fire if the phone sends a message ---
      signalingClient.on("message", (message, remoteClientId) => {
        console.log("[DEBUG] SIGNALING MESSAGE ARRIVED:", message.action, "from:", remoteClientId);
      });

      // 4. Handle SDP Offer
      signalingClient.on("sdpOffer", async (offer, remoteClientId) => {
        console.log("[MASTER] SDP Offer detected from:", remoteClientId);
        try {
          let pc = videoStream.peerConnectionByClientId[remoteClientId];

          if (!pc || pc.signalingState === 'closed') {
            pc = setupPeerConnection(remoteClientId, configuration);
          }

          await pc.setRemoteDescription(new RTCSessionDescription(offer));
          console.log("[MASTER] Remote description set for:", remoteClientId);

          // Flush any ICE candidates that arrived before setRemoteDescription completed
          const pendingCandidates = pendingIceCandidatesRef.current[remoteClientId] || [];
          if (pendingCandidates.length > 0) {
            console.log(`[MASTER] Flushing ${pendingCandidates.length} queued ICE candidates for: ${remoteClientId}`);
            for (const candidate of pendingCandidates) {
              try {
                await pc.addIceCandidate(new RTCIceCandidate(candidate));
              } catch (e) {
                console.error("[MASTER] Error adding queued ICE candidate:", e);
              }
            }
            delete pendingIceCandidatesRef.current[remoteClientId];
          }

          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);

          console.log("[MASTER] Sending Answer to:", remoteClientId);
          signalingClient.sendSdpAnswer(pc.localDescription, remoteClientId);
        } catch (err) {
          console.error("[MASTER] SDP Process Error:", err);
        }
      });

      // 5. Handle ICE Candidates
      signalingClient.on("iceCandidate", async (candidate, remoteClientId) => {
        const pc = videoStream.peerConnectionByClientId[remoteClientId];
        if (pc && pc.remoteDescription && pc.remoteDescription.type) {
          // Remote description is set -- add candidate directly
          try {
            await pc.addIceCandidate(new RTCIceCandidate(candidate));
          } catch (e) {
            console.error("[MASTER] ICE Error:", e);
          }
        } else {
          // PeerConnection not ready yet -- queue the candidate.
          // These will be flushed in the sdpOffer handler after setRemoteDescription.
          console.log("[MASTER] Queuing ICE candidate for:", remoteClientId);
          if (!pendingIceCandidatesRef.current[remoteClientId]) {
            pendingIceCandidatesRef.current[remoteClientId] = [];
          }
          pendingIceCandidatesRef.current[remoteClientId].push(candidate);
        }
      });

      signalingClient.on("open", () => {
        console.log("[MASTER] Signaling OPEN.");
        console.log("[MASTER] Listening on ARN:", videoCallData.channelArn);
      });

      signalingClient.on("error", (err) => console.error("[MASTER] Signaling Error:", err));
      signalingClient.on("close", () => console.log("[MASTER] Signaling Closed"));

      videoStream.signalingClient = signalingClient;
      signalingClient.open();

    } catch (error) {
      console.error("[MASTER] Startup Failed:", error);
    }
  };

  // Common connection state handler for both roles
  const setupConnectionStateHandlers = (userRole, clientIdKey, pc) => {
    pc.oniceconnectionstatechange = async (event) => {
      const iceState = pc.iceConnectionState;
      const connectionState = pc.connectionState;

      if (
        (iceState === "disconnected" || iceState === "failed") &&
        connectionState === "connected" &&
        remoteView
      ) {
        const retryCount = disconnectRetryCountRef.current[clientIdKey] || 0;

        console.log(`[${userRole}] Connection disconnected/failed, retry count: ${retryCount}`);

        if (disconnectTimeoutRef.current[clientIdKey]) {
          clearTimeout(disconnectTimeoutRef.current[clientIdKey]);
        }

        if (retryCount < 3) {
          disconnectRetryCountRef.current[clientIdKey] = retryCount + 1;
          console.log(`[${userRole}] Attempting to recover connection (attempt ${retryCount + 1}/3)...`);

          try {
            if (pc.signalingState !== 'stable') {
              console.warn(`[${userRole}] Signaling state not stable, waiting to attempt ICE restart...`);
            }

            await pc.restartIce();
            console.log(`[${userRole}] ICE restart initiated, waiting for reconnection...`);

            disconnectTimeoutRef.current[clientIdKey] = setTimeout(() => {
              if (pc.iceConnectionState === 'disconnected' ||
                pc.iceConnectionState === 'failed') {
                console.error(`[${userRole}] Recovery failed after ${retryCount + 1} attempts, exiting call`);
                delete disconnectRetryCountRef.current[clientIdKey];
                delete disconnectTimeoutRef.current[clientIdKey];
                exitCall();
              }
            }, 10000);
          } catch (e) {
            console.error(`[${userRole}] Failed to restart ICE:`, e);
            delete disconnectRetryCountRef.current[clientIdKey];
            delete disconnectTimeoutRef.current[clientIdKey];
            setTimeout(() => {
              exitCall();
            }, 2000);
          }
        } else {
          console.error(`[${userRole}] Max recovery attempts reached, exiting call`);
          delete disconnectRetryCountRef.current[clientIdKey];
          delete disconnectTimeoutRef.current[clientIdKey];
          setTimeout(() => {
            exitCall();
          }, 2000);
        }
      }

      if (iceState === "connected" && connectionState === "connected") {
        if (disconnectRetryCountRef.current[clientIdKey]) {
          console.log(`[${userRole}] Connection restored, resetting retry count`);
          disconnectRetryCountRef.current[clientIdKey] = 0;
        }
        if (disconnectTimeoutRef.current[clientIdKey]) {
          clearTimeout(disconnectTimeoutRef.current[clientIdKey]);
          delete disconnectTimeoutRef.current[clientIdKey];
        }

        console.log(`[${userRole}] Successfully connected, creating message channel`);
        await createMessageChannel();

        if (remoteView.current && remoteStreamRef.current) {
          if (remoteView.current.paused || remoteView.current.ended || remoteStreamRef.current.active === false) {
            remoteView.current.play().then(() => {
              console.log(`[${userRole}] Remote video resumed successfully.`);
            }).catch(e => console.error(`[${userRole}] Failed to auto-play remote video after reconnect:`, e));
          }
        }
      }

      if (iceState === "closed") {
        console.log(`[${userRole}] Connection closed, exiting call`);
        if (localView)
          setTimeout(() => {
            exitCall();
          }, 4000);
      }
    };
  };

  const handleCameraOffOn = () => {
    const _sendVideo = sendVideo;
    if (localStreamRef.current) {
      localStreamRef.current.getVideoTracks().forEach((track) => {
        track.enabled = !_sendVideo;
      });
      if (_sendVideo) sendMessage("VIDEO_OFF");
      else sendMessage("VIDEO_ON");
      setSendVideo(!_sendVideo);
    }
  };

  const handleMicOffOn = () => {
    const _sendAudio = sendAudio;
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach((track) => {
        track.enabled = !_sendAudio;
      });
      if (_sendAudio) sendMessage("AUDIO_OFF");
      else sendMessage("AUDIO_ON");
      setSendAudio(!_sendAudio);
    }
  };

  // var windowObjectReference = null;

  // for later use to open follow up notes in a new popup window
  // const openFollowUpPopup = () => {
  //   // console.log(window.location); // Removed for cleaner console
  //   if (windowObjectReference == null || windowObjectReference.closed) {
  //     let windowFeatures = "left=150,top=100,width=760,height=560, popup=true";

  //     windowObjectReference = window.open(
  //       `/video-notes/${patientId}?appointmentId=${appointmentId}`,
  //       "Doctor Notes",
  //       windowFeatures
  //     );
  //     windowObjectReference.onbeforeunload = function (e) {
  //       var dialogText = "Dialog text here";
  //       e.returnValue = dialogText;
  //       return dialogText;
  //     };
  //   } else {
  //     windowObjectReference.focus();
  //     windowObjectReference.onbeforeunload = function (e) {
  //       var dialogText = "Dialog text here";
  //       e.returnValue = dialogText;
  //       return dialogText;
  //     };
  //   }
  // };

  return (
    <>
      <Helmet>
        <title>Video Call | DOCG</title>
      </Helmet>

      <Card style={{ height: "100vh" }} className="docg-card w-100">
        <Card.Header className="d-flex justify-content-between cardHeaderUsed">
          <Card.Title className="cardTitleUsed">
            <img src={videocallIcon} alt="" className="mr-2" /> Video Call
          </Card.Title>
        </Card.Header>

        <Card.Body style={{ background: "#000000", padding: "0.5rem" }} className="docg-card-body docg-h-standard w-100 patient-channel-wrapper">
          <div className="patient-container">
            <video
              className="return-view w-100 h-100"
              ref={remoteView}
              autoPlay
              playsInline
              style={{ objectFit: "cover" }}
            />
            <div className="video-container">
              <video
                className="output-view"
                ref={localView}
                autoPlay
                playsInline
                muted
              />
            </div>
          </div>

          <div className="in-call-functions">
            <button className="" onClick={async () => { handleMicOffOn(); }}>
              {sendAudio ? (<BsFillMicFill color="#fff" size={18} />) : (<BiMicrophoneOff color="#fff" size={24} />)}
            </button>
            <button className="bg-end-call" onClick={exitCall}>
              <MdCallEnd color="#fff" size={30} />
            </button>
            <button className="" onClick={async () => { handleCameraOffOn(); }}>
              {sendVideo ? (<MdVideocam color="#fff" size={24} />) : (<MdVideocamOff color="#fff" size={24} />)}
            </button>
          </div>

        </Card.Body>
      </Card>
    </>
  );
}

export default VideoCall;